import React, { useState, useCallback, useMemo, memo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MonthPicker } from '@/components/ui/month-picker';
import { ChevronLeft, ChevronRight, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { format, addDays, startOfWeek as startOfWeekFn, endOfWeek as endOfWeekFn, isSameDay } from 'date-fns';
import { PeriodStatuses, CalendarDaysStatus } from '@/types/insights';
import { BusinessDetails, ClosureType } from '@/types/index';
import { useAuth } from '@/context/AuthContext';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { usePreparationStatusFromContext, getPeriodIndicatorColor } from '@/hooks/scheduling/usePreparationStatusFromContext';
import { useUnifiedShift } from '@/components/shifts/context/ComposedShiftProvider';
import { useBusinessCreationValidation } from '@/lib/business';
import { cn } from '@/lib/core/utils';

interface ScheduleOverviewCardProps {
  setActiveView: (view: string) => void;
  businessDetails: BusinessDetails | null;
  // DEPRECATED: These props are now handled internally by usePreparationStatus
  // Keep for backward compatibility but they are ignored
  periodStatuses?: PeriodStatuses;
  plannedShifts?: number;
  allShiftsAssigned?: boolean;
  currentViewMonth?: Date;
  onNextMonth?: () => void;
  onPreviousMonth?: () => void;
  onMonthSelect?: (date: Date) => void;
  refreshKey?: number;
  isLoading?: boolean;
  businessCreatedAt?: string;
}

const ScheduleOverviewCardInternal: React.FC<ScheduleOverviewCardProps> = ({
  setActiveView,
  businessDetails,
  // DEPRECATED props - ignored, using internal unified hook instead
  periodStatuses: _deprecatedPeriodStatuses,
  plannedShifts: _deprecatedPlannedShifts,
  allShiftsAssigned: _deprecatedAllShiftsAssigned,
  currentViewMonth: propCurrentViewMonth,
  onNextMonth,
  onPreviousMonth,
  onMonthSelect,
  refreshKey = 0,
  isLoading: propIsLoading = false,
  businessCreatedAt
}) => {
  // Component rendered
  const [internalCurrentViewMonth, setInternalCurrentViewMonth] = useState(new Date());
  const [hoveredPeriod, setHoveredPeriod] = useState<'currentWeek' | 'nextWeek' | 'currentMonth' | 'nextMonth' | null>(null);
  const hasLoadedOnceRef = useRef(false);

  // Use prop if provided, otherwise use internal state for backward compatibility
  const currentViewMonth = propCurrentViewMonth || internalCurrentViewMonth;
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { selectedShiftDate, setSelectedShiftDate } = useDashboardTab();

  // Use context-based preparation status - calculates from shifts already in context
  // NO API calls, automatically updates when shifts change
  // Pass currentViewMonth to ensure day status is calculated for the viewed month
  const {
    getDayStatus,
    periodStatuses,
    currentWeek,
    nextWeek,
    currentMonth,
    nextMonth,
    stats,
    isLoading: preparationLoading
  } = usePreparationStatusFromContext(currentViewMonth);

  // Get shifts directly from context for simple counting
  const { shifts } = useUnifiedShift();

  // Memoize shift counts by date for efficient O(1) lookups
  const shiftCountsByDate = useMemo(() => {
    const counts = new Map<string, number>();
    if (!shifts) return counts;

    shifts.forEach(shift => {
      const dateKey = format(shift.startTime, 'yyyy-MM-dd');
      const current = counts.get(dateKey) || 0;
      counts.set(dateKey, current + 1);
    });
    return counts;
  }, [shifts]);

  // Simple O(1) lookup for shift count by date
  const getShiftCountForDate = useCallback((dateKey: string): number => {
    return shiftCountsByDate.get(dateKey) || 0;
  }, [shiftCountsByDate]);

  // Combine loading states
  const isLoading = propIsLoading || preparationLoading;

  // Track if we've loaded data at least once - only show skeleton on initial load
  if (!isLoading && !hasLoadedOnceRef.current) {
    hasLoadedOnceRef.current = true;
  }

  // Only show skeleton on first load, not on subsequent refetches
  const showSkeleton = isLoading && !hasLoadedOnceRef.current;

  // Calculate derived values for backward compatibility
  const plannedShifts = currentMonth.shiftsCount + nextMonth.shiftsCount;
  const allShiftsAssigned = currentMonth.allPrepared && nextMonth.allPrepared;
  
  // Opening hours state logged
  
  // Use centralized month availability service with fallback
  // Use businessCreatedAt if provided, otherwise extract from businessDetails
  const businessCreationInput = businessCreatedAt || businessDetails?.created_at;
  const {
    isMonthNavigationDisabled,
  } = useBusinessCreationValidation(businessCreationInput);

  const handleDayClick = (date: Date) => {
    setSelectedShiftDate(date);
    setActiveView('shift-management');
  };

  const goToNextMonth = () => {
    if (onNextMonth) {
      // Use prop navigation handler when provided
      onNextMonth();
    } else {
      // Fallback to internal navigation for backward compatibility
      const nextMonth = new Date(currentViewMonth);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      setInternalCurrentViewMonth(nextMonth);
    }
  };

  const goToPreviousMonth = () => {
    if (onPreviousMonth) {
      // Use prop navigation handler when provided
      onPreviousMonth();
    } else {
      // Fallback to internal navigation for backward compatibility
      const now = new Date();
      const prevMonth = new Date(currentViewMonth);
      prevMonth.setMonth(prevMonth.getMonth() - 1);
      
      // Only allow going back if not before business creation
      const currentMonth = currentViewMonth.getMonth();
      const currentYear = currentViewMonth.getFullYear();
      if (!isMonthNavigationDisabled(currentMonth, currentYear, 'previous')) {
        setInternalCurrentViewMonth(prevMonth);
      }
    }
  };

  // Month selection handler for MonthPicker
  const handleMonthChange = (newDate: Date) => {
    if (onMonthSelect) {
      // Use external month selection when provided (for synchronization)
      onMonthSelect(newDate);
    } else {
      // Fallback to internal navigation for backward compatibility
      setInternalCurrentViewMonth(newDate);
    }
  };

  // No manual refresh needed - context-based calculation updates automatically
  // when shifts change in the ShiftDataProvider



  // Calculate period status using unified validation stats
  const getPeriodStatus = () => {
    if (stats.totalDays === 0 || plannedShifts === 0) {
      return {
        status: 'red',
        message: 'No shifts planned',
        description: 'Create shifts to cover your business hours',
        hoursStatus: 'Not covered',
        assignmentStatus: 'No assignments'
      };
    }

    const daysWithShifts = stats.greenDays + stats.orangeDays + stats.redDays;

    // Green status only if ALL days with shifts are green (fully assigned AND opening hours covered)
    if (daysWithShifts > 0 && stats.greenDays === daysWithShifts) {
      return {
        status: 'green',
        message: 'Fully scheduled',
        description: 'All shifts are assigned and opening hours covered',
        hoursStatus: 'Fully covered',
        assignmentStatus: 'Fully assigned'
      };
    }

    // Orange if we have mixed status or partial coverage
    if (stats.orangeDays > 0 || (stats.greenDays > 0 && (stats.redDays > 0 || stats.orangeDays > 0))) {
      const hasUnassignedShifts = !allShiftsAssigned;
      const hasGreenDays = stats.greenDays > 0;

      if (hasUnassignedShifts && hasGreenDays) {
        return {
          status: 'orange',
          message: 'Mixed coverage',
          description: 'Some days fully covered, others need assignments or have coverage gaps',
          hoursStatus: 'Partially covered',
          assignmentStatus: 'Partially assigned'
        };
      } else if (hasUnassignedShifts) {
        return {
          status: 'orange',
          message: 'Shifts need employees',
          description: 'Some shifts are missing employee assignments',
          hoursStatus: 'Hours planned',
          assignmentStatus: 'Partially assigned'
        };
      } else {
        return {
          status: 'orange',
          message: 'Coverage gaps',
          description: 'Shifts assigned but opening hours have coverage gaps',
          hoursStatus: 'Partially covered',
          assignmentStatus: 'Fully assigned'
        };
      }
    }

    // Red if mostly problems
    return {
      status: 'red',
      message: 'Schedule incomplete',
      description: 'Multiple days need attention for assignments or coverage',
      hoursStatus: 'Needs attention',
      assignmentStatus: 'Needs attention'
    };
  };

  const periodStatus = getPeriodStatus();

  // Generate single month calendar for display
  const getSingleMonthCalendar = () => {

    const now = new Date();

    // Current month
    const currentMonth = new Date(currentViewMonth);
    const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const currentMonthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

    const generateCalendarDays = (monthStart: Date, monthEnd: Date, targetMonth: Date) => {
      const calendarStart = startOfWeekFn(monthStart, { weekStartsOn: 1 });
      const calendarEnd = endOfWeekFn(monthEnd, { weekStartsOn: 1 });
      const days = [];


      for (let d = new Date(calendarStart); d <= calendarEnd; d = addDays(d, 1)) {
        const dayKey = format(d, 'yyyy-MM-dd');
        const validationResult = getDayStatus(dayKey);
        const isCurrentMonth = d.getMonth() === targetMonth.getMonth();
        const isPastDay = d < now && !isSameDay(d, now);

        // Determine final status based on conditions
        let finalStatus = 'gray';
        let isClosedDay = false;

        // Simple: always count shifts directly from context - works for all days
        const shiftsCount = getShiftCountForDate(dayKey);
        let closureType: ClosureType | null = null;

        if (isCurrentMonth && validationResult) {
          if (isPastDay) {
            finalStatus = 'gray'; // Past days are always gray
          } else {
            finalStatus = validationResult.status;

            // Check if day is marked as closed (business closed with no shifts)
            if (validationResult.status === 'closed') {
              finalStatus = 'closed';
              isClosedDay = true;
              closureType = validationResult.closureType || null;
            }
          }
        } else if (!isCurrentMonth) {
          finalStatus = 'gray'; // Other month days are gray
        }

        days.push({
          date: new Date(d),
          dayKey,
          status: finalStatus,
          shiftsCount,
          isToday: isSameDay(d, now),
          isCurrentMonth,
          isPastDay,
          isClosedDay,
          closureType
        });
      }

      return days;
    };

    return {
      days: generateCalendarDays(currentMonthStart, currentMonthEnd, currentMonth),
      monthName: format(currentMonth, 'MMMM yyyy')
    };
  };

  const singleCalendar = getSingleMonthCalendar();

  // Calculate date labels for the 4 periods
  const getPeriodDateLabels = () => {
    const now = new Date();

    // Current week - start from today to exclude past days
    const currentWeekStart = now;
    const currentWeekEnd = endOfWeekFn(now, { weekStartsOn: 1 });
    const currentWeekLabel = `${format(currentWeekStart, 'd')} - ${format(currentWeekEnd, 'd MMMM')}`;

    // Next week
    const nextWeekStart = addDays(currentWeekEnd, 1);
    const nextWeekEnd = endOfWeekFn(nextWeekStart, { weekStartsOn: 1 });
    const nextWeekLabel = `${format(nextWeekStart, 'd')} - ${format(nextWeekEnd, 'd MMMM')}`;

    // Current month
    const currentMonthLabel = format(now, 'MMMM');

    // Next month
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const nextMonthLabel = format(nextMonth, 'MMMM');

    return {
      currentWeek: currentWeekLabel,
      nextWeek: nextWeekLabel,
      currentMonth: currentMonthLabel,
      nextMonth: nextMonthLabel
    };
  };

  const periodDateLabels = getPeriodDateLabels();

  // Helper function to check if a day is within the hovered period
  const isDayInHoveredPeriod = (date: Date): boolean => {
    if (!hoveredPeriod) return true; // No period hovered, don't dim anything

    const now = new Date();

    switch (hoveredPeriod) {
      case 'currentWeek': {
        // Start from today to exclude past days from highlighting
        const weekEnd = endOfWeekFn(now, { weekStartsOn: 1 });
        // Include today and future days in the week (compare at day level, not datetime)
        return (isSameDay(date, now) || date > now) && date <= weekEnd;
      }
      case 'nextWeek': {
        const currentWeekEnd = endOfWeekFn(now, { weekStartsOn: 1 });
        const nextWeekStart = addDays(currentWeekEnd, 1);
        const nextWeekEnd = endOfWeekFn(nextWeekStart, { weekStartsOn: 1 });
        // Use day-level comparison to include first day of next week
        return (isSameDay(date, nextWeekStart) || date > nextWeekStart) && date <= nextWeekEnd;
      }
      case 'currentMonth': {
        const isCurrentMonth = date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        // Only highlight today and future days in the current month
        return isCurrentMonth && (isSameDay(date, now) || date > now);
      }
      case 'nextMonth': {
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        return date.getMonth() === nextMonth.getMonth() && date.getFullYear() === nextMonth.getFullYear();
      }
      default:
        return true;
    }
  };

  return (
    <Card className="flex flex-col h-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 flex-shrink-0">
        <CardTitle className="text-sm font-medium">Schedule Overview</CardTitle>
        <div className={`h-3 w-3 rounded-full ${
          periodStatus.status === 'green' ? 'bg-green-500' :
          periodStatus.status === 'orange' ? 'bg-orange-500' : 'bg-red-500'
        }`} />
      </CardHeader>
      <CardContent className={cn(
        "flex-1",
        isMobile ? "p-4" : "p-4"
      )}>
        <div className={cn(
          "h-full",
          isMobile ? "flex flex-col space-y-4" : "grid grid-cols-2 gap-6"
        )}>
          {/* Left Side - 4 Period Status Rows */}
          <div className={cn(
            "gap-2",
            isMobile
              ? "grid grid-cols-2"
              : "flex flex-col justify-between h-full"
          )}>
            {showSkeleton ? (
              <>
                {/* Skeleton for Current Week */}
                <div className={cn(
                  "flex items-center justify-between bg-sidebar/60 rounded-lg animate-pulse",
                  isMobile ? "p-3 min-h-[3rem]" : "p-4 min-h-[4rem] flex-1"
                )}>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-3 w-3 rounded-full" />
                    <div>
                      <Skeleton className="h-4 w-28 mb-2" />
                      <Skeleton className="h-3 w-36" />
                    </div>
                  </div>
                  <div className="text-right">
                    <Skeleton className="h-4 w-6 mb-1" />
                    <Skeleton className="h-3 w-10" />
                  </div>
                </div>

                {/* Skeleton for Next Week */}
                <div className={cn(
                  "flex items-center justify-between bg-sidebar/60 rounded-lg animate-pulse",
                  isMobile ? "p-3 min-h-[3rem]" : "p-4 min-h-[4rem] flex-1"
                )}>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-3 w-3 rounded-full" />
                    <div>
                      <Skeleton className="h-4 w-24 mb-2" />
                      <Skeleton className="h-3 w-36" />
                    </div>
                  </div>
                  <div className="text-right">
                    <Skeleton className="h-4 w-6 mb-1" />
                    <Skeleton className="h-3 w-10" />
                  </div>
                </div>

                {/* Skeleton for Current Month */}
                <div className={cn(
                  "flex items-center justify-between bg-sidebar/60 rounded-lg animate-pulse",
                  isMobile ? "p-3 min-h-[3rem]" : "p-4 min-h-[4rem] flex-1"
                )}>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-3 w-3 rounded-full" />
                    <div>
                      <Skeleton className="h-4 w-32 mb-2" />
                      <Skeleton className="h-3 w-36" />
                    </div>
                  </div>
                  <div className="text-right">
                    <Skeleton className="h-4 w-6 mb-1" />
                    <Skeleton className="h-3 w-10" />
                  </div>
                </div>

                {/* Skeleton for Next Month */}
                <div className={cn(
                  "flex items-center justify-between bg-sidebar/60 rounded-lg animate-pulse",
                  isMobile ? "p-3 min-h-[3rem]" : "p-4 min-h-[4rem] flex-1"
                )}>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-3 w-3 rounded-full" />
                    <div>
                      <Skeleton className="h-4 w-28 mb-2" />
                      <Skeleton className="h-3 w-36" />
                    </div>
                  </div>
                  <div className="text-right">
                    <Skeleton className="h-4 w-6 mb-1" />
                    <Skeleton className="h-3 w-10" />
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Current Week */}
                <div
                  className={cn(
                    "flex items-center justify-between bg-sidebar rounded-lg cursor-pointer",
                    isMobile ? "p-3 min-h-[3rem]" : "p-4 min-h-[4rem] flex-1"
                  )}
                  onMouseEnter={() => setHoveredPeriod('currentWeek')}
                  onMouseLeave={() => setHoveredPeriod(null)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-3 w-3 rounded-full ${getPeriodIndicatorColor(currentWeek)}`} />
                    <div>
                      <h4 className="text-sm font-semibold">Current Week - {periodDateLabels.currentWeek}</h4>
                      <p className="text-xs text-muted-foreground">
                        {!currentWeek.hasShifts ? 'No shifts planned' :
                         !currentWeek.allPrepared ? 'Partial coverage or unassigned' : 'Complete coverage & assigned'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold">{currentWeek.shiftsCount}</div>
                    <p className="text-xs text-muted-foreground">shifts</p>
                  </div>
                </div>

                {/* Next Week */}
                <div
                  className={cn(
                    "flex items-center justify-between bg-sidebar rounded-lg cursor-pointer",
                    isMobile ? "p-3 min-h-[3rem]" : "p-4 min-h-[4rem] flex-1"
                  )}
                  onMouseEnter={() => setHoveredPeriod('nextWeek')}
                  onMouseLeave={() => setHoveredPeriod(null)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-3 w-3 rounded-full ${getPeriodIndicatorColor(nextWeek)}`} />
                    <div>
                      <h4 className="text-sm font-semibold">Next Week - {periodDateLabels.nextWeek}</h4>
                      <p className="text-xs text-muted-foreground">
                        {!nextWeek.hasShifts ? 'No shifts planned' :
                         !nextWeek.allPrepared ? 'Partial coverage or unassigned' : 'Complete coverage & assigned'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold">{nextWeek.shiftsCount}</div>
                    <p className="text-xs text-muted-foreground">shifts</p>
                  </div>
                </div>

                {/* Current Month */}
                <div
                  className={cn(
                    "flex items-center justify-between bg-sidebar rounded-lg cursor-pointer",
                    isMobile ? "p-3 min-h-[3rem]" : "p-4 min-h-[4rem] flex-1"
                  )}
                  onMouseEnter={() => setHoveredPeriod('currentMonth')}
                  onMouseLeave={() => setHoveredPeriod(null)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-3 w-3 rounded-full ${getPeriodIndicatorColor(currentMonth)}`} />
                    <div>
                      <h4 className="text-sm font-semibold">Current Month - {periodDateLabels.currentMonth}</h4>
                      <p className="text-xs text-muted-foreground">
                        {!currentMonth.hasShifts ? 'No shifts planned' :
                         !currentMonth.allPrepared ? 'Partial coverage or unassigned' : 'Complete coverage & assigned'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold">{currentMonth.shiftsCount}</div>
                    <p className="text-xs text-muted-foreground">shifts</p>
                  </div>
                </div>

                {/* Next Month */}
                <div
                  className={cn(
                    "flex items-center justify-between bg-sidebar rounded-lg cursor-pointer",
                    isMobile ? "p-3 min-h-[3rem]" : "p-4 min-h-[4rem] flex-1"
                  )}
                  onMouseEnter={() => setHoveredPeriod('nextMonth')}
                  onMouseLeave={() => setHoveredPeriod(null)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-3 w-3 rounded-full ${getPeriodIndicatorColor(nextMonth)}`} />
                    <div>
                      <h4 className="text-sm font-semibold">Next Month - {periodDateLabels.nextMonth}</h4>
                      <p className="text-xs text-muted-foreground">
                        {!nextMonth.hasShifts ? 'No shifts planned' :
                         !nextMonth.allPrepared ? 'Partial coverage or unassigned' : 'Complete coverage & assigned'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold">{nextMonth.shiftsCount}</div>
                    <p className="text-xs text-muted-foreground">shifts</p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Right Side - Single Calendar */}
          <div className={cn(
            "flex flex-col",
            isMobile ? "space-y-3" : "space-y-4 h-full"
          )}>
            {showSkeleton ? (
              // Calendar loading skeleton
              <div className="flex flex-col space-y-3 animate-pulse">
                {/* Navigation skeleton */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-9 w-9 rounded-md" />
                    <Skeleton className="h-9 w-32 rounded-md" />
                    <Skeleton className="h-9 w-9 rounded-md" />
                  </div>
                </div>
                {/* Calendar grid skeleton */}
                <div className="grid grid-cols-7 gap-1.5">
                  {/* Day headers */}
                  {Array.from({ length: 7 }).map((_, i) => (
                    <Skeleton key={`header-${i}`} className="h-6 w-full" />
                  ))}
                  {/* Calendar days */}
                  {Array.from({ length: 35 }).map((_, i) => (
                    <Skeleton key={`day-${i}`} className="h-8 w-full rounded-md" />
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Navigation, Month, Legend - All in one line */}
                <div className={cn(
                  "gap-4",
                  isMobile
                    ? "flex flex-col space-y-2"
                    : "flex items-center justify-between"
                )}>
                  {/* Month Navigation - Left side */}
                  <div className={cn(
                    "flex items-center gap-2",
                    isMobile ? "justify-center" : ""
                  )}>
                    <Button
                      variant="outline"
                      size={isMobile ? "default" : "sm"}
                      onClick={(e) => { e.stopPropagation(); goToPreviousMonth(); }}
                      disabled={isMonthNavigationDisabled(currentViewMonth.getMonth(), currentViewMonth.getFullYear(), 'previous')}
                    >
                      <ChevronLeft className={cn(
                        isMobile ? "h-5 w-5" : "h-4 w-4"
                      )} />
                    </Button>
                <MonthPicker
                  value={currentViewMonth}
                  onChange={handleMonthChange}
                  businessCreationDate={businessCreationInput}
                  size={isMobile ? "default" : "sm"}
                  className="font-semibold"
                />
                <Button
                  variant="outline"
                  size={isMobile ? "default" : "sm"}
                  onClick={(e) => { e.stopPropagation(); goToNextMonth(); }}
                >
                  <ChevronRight className={cn(
                    isMobile ? "h-5 w-5" : "h-4 w-4"
                  )} />
                </Button>
              </div>

              {/* Legend - Right side */}
              <div className={cn(
                "text-xs flex-shrink-0 ",
                isMobile
                  ? "grid grid-cols-3 gap-1 justify-center text-[10px]"
                  : "hidden sm:grid grid-cols-3 gap-x-1 gap-y-1 ml-2"
              )}>
                <div className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <span>Complete</span>
                </div>

                <div className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-orange-500" />
                  <span>Partial</span>
                </div>

                <div className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-red-500" />
                  <span>No shifts</span>
                </div>

                <div className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-slate-300 dark:bg-gray-500" />
                  <span>Past</span>
                </div>

                <div className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-gray-300 dark:bg-gray-700" />
                  <span>Closed</span>
                </div>

                <div className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-pink-500" />
                  <span>Manual</span>
                </div>

              </div>
            </div>
            
            {/* Single Month Calendar */}
            <div className={cn(
              isMobile ? "space-y-2" : "space-y-3 flex-1"
            )}>
              <div className={cn(
                "grid grid-cols-7",
                isMobile ? "gap-1" : "gap-1.5"
              )}>
                {/* Day Headers */}
                <div className={cn(
                  "text-center text-muted-foreground py-1 font-medium",
                  isMobile ? "text-xs" : "text-xs"
                )}>{isMobile ? 'M' : 'Mon'}</div>
                <div className={cn(
                  "text-center text-muted-foreground py-1 font-medium",
                  isMobile ? "text-xs" : "text-xs"
                )}>{isMobile ? 'T' : 'Tue'}</div>
                <div className={cn(
                  "text-center text-muted-foreground py-1 font-medium",
                  isMobile ? "text-xs" : "text-xs"
                )}>{isMobile ? 'W' : 'Wed'}</div>
                <div className={cn(
                  "text-center text-muted-foreground py-1 font-medium",
                  isMobile ? "text-xs" : "text-xs"
                )}>{isMobile ? 'T' : 'Thu'}</div>
                <div className={cn(
                  "text-center text-muted-foreground py-1 font-medium",
                  isMobile ? "text-xs" : "text-xs"
                )}>{isMobile ? 'F' : 'Fri'}</div>
                <div className={cn(
                  "text-center text-muted-foreground py-1 font-medium",
                  isMobile ? "text-xs" : "text-xs"
                )}>{isMobile ? 'S' : 'Sat'}</div>
                <div className={cn(
                  "text-center text-muted-foreground py-1 font-medium",
                  isMobile ? "text-xs" : "text-xs"
                )}>{isMobile ? 'S' : 'Sun'}</div>

                {/* Calendar Days - Always show exactly 42 cells (6 rows) */}
                {Array.from({ length: 42 }, (_, index) => {
                  const day = singleCalendar.days[index];
                  if (!day) {
                    // Empty cell for consistent 6-row layout
                    return <div key={`empty-${index}`} className={cn(
                      isMobile ? "h-8" : "h-8"
                    )} />;
                  }
                  

                  const isInHoveredPeriod = isDayInHoveredPeriod(day.date);
                  const shouldDim = hoveredPeriod && !isInHoveredPeriod;

                  return (
                    <div
                      key={day.dayKey}
                      className={cn(
                        "relative rounded-md flex items-center justify-center font-medium cursor-pointer transition-all duration-200 hover:scale-105",
                        isMobile ? "h-10 text-sm min-h-[40px]" : "h-8 text-xs",
                        !day.isCurrentMonth ? 'text-gray-300 dark:text-gray-600' :
                          day.isToday ? 'ring-2 ring-primary ring-offset-1 dark:ring-offset-background' : '',
                        !day.isCurrentMonth ? '' :
                          day.status === 'red' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                          day.status === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' :
                          day.status === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                          day.status === 'closed' && day.closureType === 'custom_closed_date' ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300' :
                          day.status === 'closed' ? 'bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300' :
                          day.status === 'gray' ? 'bg-slate-100 dark:bg-gray-500 text-slate-500 dark:text-gray-200' :
                          'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400',
                        shouldDim ? 'opacity-50' : ''
                      )}
                      title={`${format(day.date, 'MMM d')}: ${day.shiftsCount} shifts ${
                        day.status === 'green' ? '(complete coverage & assigned)' :
                        day.status === 'orange' ? '(partial coverage or unassigned)' :
                        day.status === 'closed' && day.closureType === 'custom_closed_date' ? '(manually closed)' :
                        day.status === 'closed' ? '(weekly schedule - closed)' :
                        day.status === 'gray' ? '(past day)' :
                        '(no shifts planned)'
                      }`}
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        if (day.isCurrentMonth && !day.isPastDay) {
                          handleDayClick(day.date);
                        }
                      }}
                    >
                      {format(day.date, 'd')}
                      {day.shiftsCount > 0 && (
                        <div className={cn(
                          "absolute -top-1 -right-1 bg-primary rounded-full text-primary-foreground flex items-center justify-center",
                          isMobile ? "h-5 w-5 text-xs" : "h-4 w-4 text-[10px]"
                        )}>
                          {day.shiftsCount > 9 ? '9+' : day.shiftsCount}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// PERFORMANCE FIX: Custom comparison function to prevent unnecessary re-renders
const arePropsEqual = (prevProps: ScheduleOverviewCardProps, nextProps: ScheduleOverviewCardProps): boolean => {
  // Now that we use internal hook, only compare essential props
  return (
    // Critical props that affect rendering
    prevProps.businessDetails?.id === nextProps.businessDetails?.id &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.refreshKey === nextProps.refreshKey &&
    // Date comparisons
    (prevProps.currentViewMonth?.getTime() === nextProps.currentViewMonth?.getTime() ||
     (!prevProps.currentViewMonth && !nextProps.currentViewMonth)) &&
    prevProps.businessCreatedAt === nextProps.businessCreatedAt
  );
};

// Memoized component to prevent unnecessary re-renders
const MemoizedScheduleOverviewCardInternal = memo(ScheduleOverviewCardInternal, arePropsEqual);

// Error boundary wrapper for the component
const ScheduleOverviewCard: React.FC<ScheduleOverviewCardProps> = (props) => {
  try {
    return <MemoizedScheduleOverviewCardInternal {...props} />;
  } catch (error) {
    console.error('ScheduleOverviewCard render error:', error);
    return (
      <Card className="flex flex-col h-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">Schedule Overview</CardTitle>
          <div className="h-3 w-3 rounded-full bg-gray-400" />
        </CardHeader>
        <CardContent className="flex-1 p-6">
          <div className="text-center text-muted-foreground">
            <p>Unable to load schedule overview</p>
            <p className="text-xs mt-1">Please refresh the page</p>
          </div>
        </CardContent>
      </Card>
    );
  }
};

export default ScheduleOverviewCard;